<template>
  <!-- Mode popup compact (sans el-form-item) -->
  <div v-if="isPopupMode" class="date-range-popup" @click.stop>
    <!-- DatePicker Element-UI natif -->
    <div>
      <el-date-picker
        type="daterange"
        :value="dateRangeValue"
        :start-placeholder="'Date de début'"
        :end-placeholder="'Date de fin'"
        @input="handleDateChange"
        format="dd/MM/yyyy"
        :picker-options="pickerOptions"
        size="mini"
        style="width: 100%;"
        :append-to-body="false"
        :popper-class="'inline-date-picker'"
        :unlink-panels="false"
      />

      <!-- Raccourcis de dates -->
      <div style="margin-top: 12px; margin-bottom: 8px;">
        <div style="font-size: 12px; color: #606266; margin-bottom: 8px; text-align: center; font-weight: 500;">Raccourcis rapides :</div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
          <el-link type="primary" @click="setDateShortcut('lastWeek')" style="font-size: 13px; text-align: center;">{{ $t('general.calendar.lastWeek') }}</el-link>
          <el-link type="primary" @click="setDateShortcut('lastMonth')" style="font-size: 13px; text-align: center;">{{ $t('general.calendar.lastMonth') }}</el-link>
          <el-link type="primary" @click="setDateShortcut('lastThreeMonths')" style="font-size: 13px; text-align: center;">{{ $t('general.calendar.lastThreeMonths') }}</el-link>
          <el-link type="primary" @click="setDateShortcut('lastSixMonths')" style="font-size: 13px; text-align: center;">{{ $t('general.calendar.lastSixMonths') }}</el-link>
        </div>
      </div>

      <!-- Boutons d'action -->
      <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 12px; padding-top: 8px; border-top: 1px solid #f0f0f0;">
        <el-link @click="clearDates()" type="info" :underline="false" style="font-size: 12px;">
          <i class="el-icon-delete"></i> Effacer tout
        </el-link>
        <el-button size="small" @click="handleValidate" type="primary" style="font-size: 13px;">
          <i class="el-icon-check"></i> Valider
        </el-button>
      </div>
    </div>
  </div>

  <!-- Mode formulaire normal (avec el-form-item) -->
  <el-form-item 
    v-else
    :label="$t(field.label)" 
    :prop="field.name"
    :class="fieldClass"
  >
    <el-date-picker
      :value="dateRangeValue"
      @input="handleDateChange"
      type="daterange"
      :start-placeholder="$t(getProperty('startPlaceholder', 'general.filters.dateCreationStart'))"
      :end-placeholder="$t(getProperty('endPlaceholder', 'general.filters.dateCreationEnd'))"
      :format="getProperty('format', 'dd/MM/yyyy')"
      :value-format="getProperty('valueFormat', 'yyyy-MM-dd')"
      :size="getProperty('size', 'medium')"
      :disabled="field.isDisabled"
      :clearable="getProperty('clearable', true)"
      :picker-options="pickerOptions"
      :range-separator="getProperty('rangeSeparator', '-')"
      :append-to-body="getProperty('appendToBody', false)"
      :popper-append-to-body="getProperty('popperAppendToBody', false)"
      style="width: 100%"
    />
    
    <!-- Raccourcis rapides -->
    <div v-if="getProperty('showShortcuts', false)" class="date-shortcuts">
      <div class="shortcuts-label">{{ $t('components.searchField.quickShortcuts') }}</div>
      <div class="shortcuts-buttons">
        <el-button
          v-for="shortcut in dateShortcuts"
          :key="shortcut.key"
          size="mini"
          type="text"
          @click="setDateShortcut(shortcut.key)"
          class="shortcut-button"
        >
          {{ shortcut.label }}
        </el-button>
      </div>
    </div>
    
    <!-- Aide contextuelle -->
    <div v-if="field.helpText" class="field-help-text">
      <small class="text-muted">{{ $t(field.helpText) }}</small>
    </div>
  </el-form-item>
</template>

<script>
export default {
  name: 'GenericFieldDateRange',
  props: {
    field: {
      type: Object,
      required: true
    },
    value: {
      type: [Array, Object],
      default: () => null
    },
    isPopupMode: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    fieldClass() {
      return {
        'search-field': true,
        'search-field-daterange': true,
        'search-field-required': this.field.isRequired,
        'search-field-disabled': this.field.isDisabled
      }
    },
    
    dateRangeValue() {
      // Conversion de la valeur selon le format attendu
      if (!this.value) {
        return null
      }

      // Si c'est un objet avec startDate/endDate
      if (typeof this.value === 'object' && !Array.isArray(this.value)) {
        const start = this.value.startDate || this.value.dateCreationGt
        const end = this.value.endDate || this.value.dateCreationLt
        return start && end ? [start, end] : null
      }

      // Si c'est un tableau
      if (Array.isArray(this.value)) {
        return this.value.length === 2 ? this.value : null
      }

      return null
    },

    pickerOptions() {
      const options = {
        shortcuts: [], // Aucun raccourci dans le calendrier Element UI
        footerFormat: '', // Masquer le footer avec les boutons par défaut
        unlinkPanels: false, // Garder les panneaux liés pour l'affichage horizontal
        firstDayOfWeek: 1 // Commencer la semaine par lundi
      }

      // Limites de dates
      const disabledDate = this.getProperty('disabledDate')
      if (disabledDate === 'future') {
        options.disabledDate = (time) => {
          return time.getTime() > Date.now()
        }
      } else if (disabledDate === 'past') {
        options.disabledDate = (time) => {
          return time.getTime() < Date.now() - 8.64e7 // 24 hours
        }
      }

      return options
    },

    dateShortcuts() {
      return [
        { key: 'lastWeek', label: this.$t('general.calendar.lastWeek') },
        { key: 'lastMonth', label: this.$t('general.calendar.lastMonth') },
        { key: 'lastThreeMonths', label: this.$t('general.calendar.lastThreeMonths') },
        { key: 'lastSixMonths', label: this.$t('general.calendar.lastSixMonths') }
      ]
    }
  },
  methods: {
    handleDateChange(dateRange) {
      if (!dateRange || !Array.isArray(dateRange)) {
        this.emitChange(null, null)
        return
      }

      const [startDate, endDate] = dateRange
      // Formater les dates au format YYYY-MM-DD pour cohérence
      const formattedStartDate = this.formatDateToString(startDate)
      const formattedEndDate = this.formatDateToString(endDate)
      this.emitChange(formattedStartDate, formattedEndDate)
    },

    emitChange(startDate, endDate) {
      console.log('🔵 [GenericFieldDateRange.emitChange]', { field: this.field.name, startDate, endDate, isPopupMode: this.isPopupMode })
      
      // Format selon les besoins du champ
      const fieldName = this.field.name
      let emittedValue

      if (this.isPopupMode) {
        // Toujours format tableau en mode popup (pour ApiSearchForm.vue)
        emittedValue = startDate && endDate ? [startDate, endDate] : null
      } else if (fieldName === 'dateCreation') {
        // Format pour dateCreation (dateCreationGt, dateCreationLt) en mode formulaire
        emittedValue = {
          dateCreationGt: startDate,
          dateCreationLt: endDate
        }
      } else {
        // Format tableau par défaut
        emittedValue = startDate && endDate ? [startDate, endDate] : null
      }
      
      this.$emit('input', emittedValue)
      this.$emit('change', { 
        field: this.field.name, 
        value: emittedValue,
        startDate,
        endDate
      })
      
      // IMPORTANT: Émettre range-update pour ApiSearchForm
      // ApiSearchForm a besoin de cet événement pour gérer l'eventMapping (beforeContacts/afterContacts)
      this.$emit('range-update', {
        field: fieldName,
        minValue: startDate,
        maxValue: endDate,
        minField: null, // Sera défini par eventMapping dans le backend
        maxField: null  // Sera défini par eventMapping dans le backend
      })
      
      console.log('✅ [GenericFieldDateRange.emitChange] Émissions:', { 
        input: emittedValue,
        'range-update': { field: fieldName, minValue: startDate, maxValue: endDate }
      })
    },

    setDateShortcut(shortcut) {
      const endDate = new Date()
      let startDate

      switch (shortcut) {
        case 'lastWeek':
          // 7 derniers jours
          startDate = new Date()
          startDate.setDate(startDate.getDate() - 7)
          break
        case 'lastMonth':
          // 30 derniers jours
          startDate = new Date()
          startDate.setMonth(startDate.getMonth() - 1)
          break
        case 'lastThreeMonths':
          // 3 derniers mois
          startDate = new Date()
          startDate.setMonth(startDate.getMonth() - 3)
          break
        case 'lastSixMonths':
          // 6 derniers mois
          startDate = new Date()
          startDate.setMonth(startDate.getMonth() - 6)
          break
        default:
          return
      }

      // Formater les dates au format YYYY-MM-DD pour cohérence avec la sélection manuelle
      const formattedStartDate = this.formatDateToString(startDate)
      const formattedEndDate = this.formatDateToString(endDate)
      
      this.emitChange(formattedStartDate, formattedEndDate)
      
      // En mode popup, fermer et déclencher la recherche après avoir sélectionné un preset
      if (this.isPopupMode) {
        this.$emit('close-and-search')
      }
    },
    
    formatDateToString(date) {
      if (!date) return null
      if (typeof date === 'string') return date
      
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    clearDates() {
      this.emitChange(null, null)
    },

    handleValidate() {
      // Émettre l'événement pour fermer la popup et déclencher la recherche
      this.$emit('close-and-search')
    },

    getProperty(propertyName, defaultValue = undefined) {
      if (!this.field.properties) {
        return defaultValue
      }
      
      // Support pour les propriétés en tant qu'objet ou tableau
      if (typeof this.field.properties === 'object' && !Array.isArray(this.field.properties)) {
        return this.field.properties[propertyName] || defaultValue
      }
      
      if (Array.isArray(this.field.properties)) {
        const property = this.field.properties.find(p => p.name === propertyName)
        if (!property) {
          return defaultValue
        }

        switch (property.type) {
          case 'boolean':
            return property.value === 'true' || property.value === true
          case 'number':
          case 'integer':
            return property.value ? Number(property.value) : defaultValue
          case 'string':
          default:
            return property.value || defaultValue
        }
      }

      return defaultValue
    }
  }
}
</script>

<style scoped>
.search-field {
  margin-bottom: 16px;
}

.search-field-required .el-form-item__label::after {
  content: ' *';
  color: #f56c6c;
}

.search-field-disabled {
  opacity: 0.6;
}

.field-help-text {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.4;
}

.text-muted {
  color: #909399;
}

/* Styles pour les raccourcis de dates */
.date-shortcuts {
  margin-top: 12px;
  padding: 8px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.shortcuts-label {
  font-size: 11px;
  color: #606266;
  margin-bottom: 4px;
  font-weight: 500;
}

.shortcuts-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.shortcut-button {
  font-size: 11px !important;
  padding: 2px 6px !important;
  height: auto !important;
  line-height: 1.3 !important;
  color: #409eff !important;
}

.shortcut-button:hover {
  color: #66b1ff !important;
  background-color: rgba(64, 158, 255, 0.1) !important;
}

/* Styles du DatePicker */
.search-field-daterange .el-date-editor {
  width: 100%;
}

.search-field-daterange .el-date-editor .el-input__inner {
  font-size: 14px;
  height: 36px;
  line-height: 36px;
}

.search-field-daterange .el-date-editor--small .el-input__inner {
  height: 32px;
  line-height: 32px;
}

/* Styles pour le mode popup */
.date-range-popup {
  background: transparent;
  border: none;
  padding: 0;
  box-shadow: none;
  position: relative;
  width: 100%;
  max-width: 100%;
}

/* Forcer le DatePicker à ne pas déborder */
.date-range-popup .el-date-editor {
  width: 100% !important;
  max-width: 100% !important;
}

.date-range-popup .el-range-editor {
  width: 100% !important;
  max-width: 100% !important;
}

.date-range-popup .el-date-editor .el-range__icon,
.date-range-popup .el-date-editor .el-range__close-icon {
  display: none !important;
}


/* Styles responsifs */
@media (max-width: 768px) {
  .shortcuts-buttons {
    flex-direction: column;
    gap: 2px;
  }
  
  .shortcut-button {
    width: 100%;
    text-align: left;
    justify-content: flex-start;
  }
  
  .shortcuts-buttons-popup {
    flex-direction: column;
    gap: 2px;
  }
  
  .shortcut-button-popup {
    width: 100%;
    text-align: left;
    justify-content: flex-start;
  }
}
</style>

<style>
/* Style pour le datePicker Element-UI inline */
.inline-date-picker {
  z-index: 999999 !important;
}

/* Masquer les boutons en double du calendrier Element UI */
.inline-date-picker .el-picker-panel__footer {
  display: none !important;
}

/* Masquer les boutons "Effacer" et "Confirmer" du calendrier natif */
.inline-date-picker .el-picker-panel__btn {
  display: none !important;
}

/* SUPPRIMER COMPLÈTEMENT LA SIDEBAR ET RÉCUPÉRER L'ESPACE */
.inline-date-picker .el-picker-panel__sidebar {
  display: none !important;
  width: 0 !important;
}

.inline-date-picker .el-picker-panel__body-wrapper {
  margin-left: 0 !important;
  width: 100% !important;
}

.inline-date-picker .el-picker-panel__body {
  margin-left: 0 !important;
  width: 100% !important;
  display: flex !important;
  flex-direction: row !important;
}

/* Forcer la suppression de la classe has-sidebar */
.inline-date-picker.has-sidebar .el-picker-panel__body {
  margin-left: 0 !important;
}

/* FORCER L'AFFICHAGE HORIZONTAL DES DEUX CALENDRIERS */
.inline-date-picker .el-picker-panel__content.el-date-range-picker__content {
  display: inline-block !important;
  width: 285px !important;
  min-width: 285px !important;
  vertical-align: top !important;
  margin: 0 1px !important;
  overflow: visible !important;
}

.inline-date-picker .el-picker-panel__content.is-left {
  float: left !important;
}

.inline-date-picker .el-picker-panel__content.is-right {
  float: right !important;
}

/* Optimiser l'espacement du calendrier */
.inline-date-picker .el-picker-panel {
  padding: 2px !important;
  width: 100% !important;
  min-width: 425px !important;
}

/* Optimiser les en-têtes - GARDER LA STRUCTURE ELEMENT UI */
.inline-date-picker .el-date-range-picker__header {
  padding: 6px 8px !important;
  text-align: center !important;
  position: relative !important;
  min-height: 32px !important;
}

.inline-date-picker .el-date-range-picker__header div {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #606266 !important;
  display: inline-block !important;
  min-width: 120px !important;
}

/* Repositionner les boutons de navigation correctement */
.inline-date-picker .el-picker-panel__content.is-left .el-picker-panel__icon-btn.el-icon-d-arrow-left,
.inline-date-picker .el-picker-panel__content.is-left .el-picker-panel__icon-btn.el-icon-arrow-left {
  position: absolute !important;
  left: 4px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
}

.inline-date-picker .el-picker-panel__content.is-right .el-picker-panel__icon-btn.el-icon-d-arrow-right,
.inline-date-picker .el-picker-panel__content.is-right .el-picker-panel__icon-btn.el-icon-arrow-right {
  position: absolute !important;
  right: 4px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
}

.inline-date-picker .el-picker-panel__content.is-left .el-picker-panel__icon-btn.el-icon-d-arrow-left {
  left: 4px !important;
}

.inline-date-picker .el-picker-panel__content.is-left .el-picker-panel__icon-btn.el-icon-arrow-left {
  left: 28px !important;
}

.inline-date-picker .el-picker-panel__content.is-right .el-picker-panel__icon-btn.el-icon-arrow-right {
  right: 28px !important;
}

.inline-date-picker .el-picker-panel__content.is-right .el-picker-panel__icon-btn.el-icon-d-arrow-right {
  right: 4px !important;
}

/* Optimiser les boutons de navigation */
.inline-date-picker .el-picker-panel__icon-btn {
  padding: 6px !important;
  width: 20px !important;
  height: 20px !important;
  border-radius: 2px !important;
}
</style>